import { ELEMENT_TYPE, ElementTypesMap } from 'embed_v2/types/version'
import { useEffect, useRef } from 'preact/hooks'
import { widthToValue } from 'core/helpers/styles'
import emitter from '../../services/emitter'
import Badge from '../elements/Badge'
import Bullet from '../elements/Bullet'
import Button from '../elements/Button'
import Checkbox from '../elements/Checkbox'
import Datetime from '../elements/Datetime'
import Dropdown from '../elements/Dropdown'
import EmbedCode from '../elements/EmbedCode'
import Hosts from '../elements/Hosts'
import Icon from '../elements/Icon'
import Image from '../elements/Image'
import ImageFeature from '../elements/ImageFeature'
import Input from '../elements/Input'
import Logo from '../elements/Logo'
import Quiz from '../elements/Quiz'
import Reminder from '../elements/Reminder'
import Text from '../elements/Text'
import Textarea from '../elements/Textarea'
import Timer from '../elements/Timer'
import Video from '../elements/Video'
import WebinarLink from '../elements/WebinarLink'

type Props<T extends keyof ElementTypesMap> = {
  element: ElementTypesMap[T]
}

const Element = <T extends keyof ElementTypesMap>(props: Props<T>) => {
  const { element } = props
  const { id, type, title, options } = element
  const node = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const showElement = () => {
      if (node.current) {
        node.current.style.opacity = '1'
      }
    }

    if (window._wf_fonts_active) {
      showElement()
    } else {
      emitter.on('webfont-active', showElement)
    }
  }, [id])

  const styles = {
    display: 'flex',
    flexDirection: 'column',
    transition: 'opacity 90ms',
    opacity: 0,
    boxSizing: 'border-box',
    height: 'auto',
    width: widthToValue(options.width, options.width_fixed, options.width_percent),
    marginTop: `${options.margin_top}px`,
    marginLeft: options.align === 'left' ? '0' : 'auto',
    marginRight: options.align === 'right' ? '0' : 'auto'
  }

  return (
    <div
      id={`wf_element_${id}`}
      className={`wf_element wf_element_${id} wf_reset`}
      ref={node}
      style={styles}
      data-title={title}
    >
      {(() => {
        switch (type) {
          case ELEMENT_TYPE.Text:
            return <Text options={options} />
          case ELEMENT_TYPE.Header:
            return <Text options={options} />
          case ELEMENT_TYPE.Video:
            return <Video options={options} />
          case ELEMENT_TYPE.Button:
            return <Button options={options} />
          case ELEMENT_TYPE.Datetime:
            return <Datetime options={options} />
          case ELEMENT_TYPE.Timer:
            return <Timer options={options} />
          case ELEMENT_TYPE.Image:
            return <Image options={options} />
          case ELEMENT_TYPE.Hosts:
            return <Hosts options={options} />
          case ELEMENT_TYPE.Icon:
            return <Icon options={options} />
          case ELEMENT_TYPE.Reminder:
            return <Reminder options={options} />
          case ELEMENT_TYPE.Quiz:
            return <Quiz options={options} />
          case ELEMENT_TYPE.WebinarLink:
            return <WebinarLink options={options} />
          case ELEMENT_TYPE.Dropdown:
            return <Dropdown options={options} />
          case ELEMENT_TYPE.Input:
            return <Input options={options} />
          case ELEMENT_TYPE.Textarea:
            return <Textarea options={options} />
          case ELEMENT_TYPE.Checkbox:
            return <Checkbox options={options} />
          case ELEMENT_TYPE.Logo:
            return <Logo options={options} />
          case ELEMENT_TYPE.EmbedCode:
            return <EmbedCode options={options} />
          case ELEMENT_TYPE.Bullet:
            return <Bullet options={options} />
          case ELEMENT_TYPE.ImageFeature:
            return <ImageFeature options={options} />
          case ELEMENT_TYPE.Badge:
            return <Badge options={options} />
          default:
            // oxlint-disable-next-line no-console
            console.error('Unknown Type: ', type)
        }
      })()}
    </div>
  )
}

export default Element
